import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, MailX, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Unsubscribe() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [done, setDone] = useState(false);
    const [reason, setReason] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const data = await api.outreachUnsubInfo(token);
                setInfo(data);
            } catch {
                setInfo({ valid: false });
            } finally { setLoading(false); }
        })();
    }, [token]);

    const confirm = async () => {
        setConfirming(true);
        try {
            await api.outreachUnsubConfirm(token, reason.trim() || null);
            setDone(true);
            toast.success("Unsubscribed");
        } catch (e) {
            toast.error(e.message || "Unsubscribe failed");
        } finally { setConfirming(false); }
    };

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main grid place-items-center px-6">
            <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                data-testid="unsubscribe-card"
                className="w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-8 text-center"
            >
                {loading ? (
                    <div className="flex items-center justify-center gap-2 text-text-secondary">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Loading…</span>
                    </div>
                ) : !info?.valid ? (
                    <>
                        <div className="mx-auto h-12 w-12 rounded-full bg-[var(--color-alert)]/15 grid place-items-center">
                            <AlertTriangle className="h-5 w-5 text-[var(--color-alert)]" />
                        </div>
                        <h1 data-testid="unsub-invalid-title" className="mt-5 text-xl font-semibold">
                            Link non valido
                        </h1>
                        <p className="mt-2 text-sm text-text-secondary">
                            Il link di disiscrizione non è valido o è scaduto.
                        </p>
                        <Link
                            to="/"
                            className="mt-6 inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-5 py-2 text-sm text-text-secondary hover:text-text-main"
                            data-testid="unsub-home-link"
                        >
                            Torna alla home
                        </Link>
                    </>
                ) : done || info.already ? (
                    <>
                        <div className="mx-auto h-12 w-12 rounded-full bg-[var(--color-success)]/15 grid place-items-center">
                            <CheckCircle2 className="h-5 w-5 text-[var(--color-success)]" />
                        </div>
                        <h1 data-testid="unsub-done-title" className="mt-5 text-xl font-semibold">
                            Sei stato rimosso
                        </h1>
                        <p className="mt-2 text-sm text-text-secondary">
                            <strong className="text-text-main">{info.email}</strong> non riceverà più email da questa mailing list.
                        </p>
                        <Link
                            to="/"
                            className="mt-6 inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2 text-sm font-medium text-white"
                            data-testid="unsub-home-link"
                        >
                            Torna alla home
                        </Link>
                    </>
                ) : (
                    <>
                        <div className="mx-auto h-12 w-12 rounded-full bg-[var(--color-panel-ai)] grid place-items-center">
                            <MailX className="h-5 w-5 text-[var(--color-secondary)]" />
                        </div>
                        <h1 data-testid="unsub-confirm-title" className="mt-5 text-xl font-semibold">
                            Sei sicuro di volerti disiscrivere?
                        </h1>
                        <p className="mt-2 text-sm text-text-secondary">
                            <strong className="text-text-main">{info.email}</strong> non riceverà più email da questa mailing list.
                        </p>
                        {info.campaign_name && (
                            <p
                                data-testid="unsub-campaign-name"
                                className="mt-3 inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-1 text-[11px] text-text-secondary"
                            >
                                <span className="uppercase tracking-widest text-[9px] text-text-secondary/60">
                                    Campaign
                                </span>
                                <span className="text-text-main truncate max-w-[220px]">
                                    {info.campaign_name}
                                </span>
                            </p>
                        )}
                        <label className="block mt-5 text-left">
                            <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                                Motivo (opzionale)
                            </span>
                            <textarea
                                data-testid="unsub-reason-input"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                rows={2}
                                maxLength={500}
                                placeholder="Es. non sono interessato al momento"
                                className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-primary)]/60"
                            />
                        </label>
                        <div className="mt-5 flex justify-center gap-2">
                            <Link
                                to="/"
                                data-testid="unsub-cancel"
                                className="rounded-full border border-[var(--color-border)] px-5 py-2 text-sm text-text-secondary hover:text-text-main"
                            >
                                Annulla
                            </Link>
                            <button
                                data-testid="unsub-confirm-btn"
                                onClick={confirm}
                                disabled={confirming}
                                className="inline-flex items-center gap-2 rounded-full bg-[var(--color-alert)] px-5 py-2 text-sm font-medium text-white disabled:opacity-60"
                            >
                                {confirming && <Loader2 className="h-4 w-4 animate-spin" />}
                                Conferma disiscrizione
                            </button>
                        </div>
                    </>
                )}
            </motion.div>
        </div>
    );
}
